<template>
  <v-layout align-center class="heatmap-toolbar-container elevation-2">
    <treeselect
      v-model="selectedLayer"
      :options="layerOptions"
      disableFuzzyMatching
      style="width: 13%"
      class="ml-1"
    />

    <treeselect
      v-model="selectedAttribute"
      :options="attributeOptions"
      :load-options="loadAttributeOptions"
      :disabled="!selectedLayer"
      :auto-load-root-options="true"
      :disable-branch-nodes="true"
      disableFuzzyMatching
      style="width: 25%"
      class="ml-1"
    />

    <span class="ml-2 pb-1"> Σ </span>
    <v-switch
      :loading="accumulateLoading"
      color="accent"
      v-model="isToAccumulate"
      :disabled="accumulateLoading || !selectedLayer || !selectedAttribute"
    />

    <v-slider
      v-model="indexSelectedDate"
      step="1"
      thumb-label
      color="accent"
      :max="sliderMax"
      :disabled="
        accumulateLoading ||
          !selectedLayer ||
          !selectedAttribute ||
          dates.length === 0
      "
      track-color="grey"
      dense
      class="ml-1"
    >
      <template v-slot:thumb-label="{ value }">
        {{ dates[indexSelectedDate] }}
      </template>
    </v-slider>
  </v-layout>
</template>

<script>
import { mapActions } from "vuex";

export default {
  name: "HeatmapToolbar",

  data() {
    return {
      /**
       * Layer selected by the user in treeselect
       */
      selectedLayer: null,

      /**
       * Attribute selected by the user in treeselect
       */
      selectedAttribute: null,

      /**
       * list of attributes returned in LayerAttributes request
       */
      attributeOptions: null,

      /**
       * Defines if the values are to accumulate until the selected date
       */
      isToAccumulate: false,

      /**
       * Defines if the switch is loading.
       * Whenever the variable isToAccumulate is changed the value becomes true.
       * It only goes to false when the necessary calculations are finished
       */
      accumulateLoading: false,

      /**
       * Objects that are returned in the request fetchObjectInWorkspaceOrderBy ttribute
       */
      allObjects: null,

      /**
       * All the dates that are returned in the fetchObjectInWorkspaceOrderByAttribute request.
       These dates are the values of the selected attribute of the objects
       */
      dates: [],

      /**
       * Index of selected date
       */
      indexSelectedDate: 0
    };
  },

  computed: {
    /**
     * Layer options available for the heatmap
     *
     * @returns {[{id: string, label: string}, {id: string, label: string}, {id: string, label: string}]}
     */
    layerOptions() {
      return [
        { id: "fc_pole", label: this.$i18n.tc("Pole", 1) },
        { id: "fc_building", label: this.$i18n.tc("Building", 1) },
        { id: "fc_manhole", label: this.$i18n.tc("Manhole", 1) }
      ];
    },

    /**
     * Define slider max
     * @returns {number}
     */
    sliderMax() {
      if (this.dates && this.dates.length === 0) {
        return 0;
      } else if (this.dates && this.dates.length > 0) {
        return this.dates.length - 1;
      }
    }
  },

  watch: {
    /**
     * Whenever the layer is changed, a new LayerAttributes request must be made
     * @param value
     */
    selectedLayer(value) {
      this.selectedAttribute = null;
      this.attributeOptions = null;
      this.allObjects = null;
      this.indexSelectedDate = 0;
      this.dates = [];
      this.$emit("clear-heatmap");
      if (value) this.loadAttributeOptions();
    },

    /**
     * Whenever the attribute is changed, a fetchObjectInWorkspaceOrderByAttribute request must be made
     * @param value
     */
    selectedAttribute(value) {
      this.dates = [];
      this.allObjects = null;
      this.indexSelectedDate = 0;
      this.$emit("clear-heatmap");
      if (value) this.getObjectInWorkspaceOrderByAttribute();
    },

    /**
     * Whenever index changes call generateDataToSendHeatMap function
     */
    indexSelectedDate() {
      this.generateDataToSendHeatMap();
    },

    /**
     * Whenever isToAccumulate changes call generateDataToSendHeatMap function
     */
    isToAccumulate() {
      this.generateDataToSendHeatMap();
    }
  },

  methods: {
    ...mapActions([
      "GET_LAYER_ATTRIBUTES",
      "fetchObjectInWorkspaceOrderByAttribute"
    ]),

    /**
     * Assigns fetched result into an object array with id and label to be used in computed property for tree select.
     * @returns {Promise<void>}
     */
    async loadAttributeOptions() {
      if (this.selectedLayer) {
        try {
          let optionsAttributes = {
            attributes: [],
            extra_fields: [],
            custom_fields: []
          };

          let options = await this.GET_LAYER_ATTRIBUTES({
            layer: this.selectedLayer,
            filter: "date"
          });
          let filtered = this.removeListItems(options.attributes);

          filtered.forEach(item => {
            optionsAttributes.attributes.push({
              id: "attributes:" + item,
              label: this.formatText(item)
            });
          });

          options.custom_fields.forEach(item => {
            optionsAttributes.custom_fields.push({
              id: "custom_fields:" + item.uuid,
              label: item.label
            });
          });

          options.extra_fields.forEach(item => {
            optionsAttributes.extra_fields.push({
              id: "extra_fields:" + item.name,
              label: this.formatText(item.name, "extraFields")
            });
          });

          let finalList = [];
          if (optionsAttributes.attributes.length > 0) {
            finalList.push({
              id: "attributes",
              label: this.$i18n.tc('Attributes',1),
              children: optionsAttributes.attributes
            });
          }
          if (optionsAttributes.custom_fields.length > 0) {
            finalList.push({
              id: "custom_fields",
              label: this.$i18n.tc('CustomField',1),
              children: optionsAttributes.custom_fields
            });
          }

          if (optionsAttributes.extra_fields.length > 0) {
            finalList.push({
              id: "extra_fields",
              label: this.$i18n.tc('ExtraFields',1),
              children: optionsAttributes.extra_fields
            });
          }

          this.attributeOptions = finalList;
        } catch (e) {
          console.error(e);
        }
      }
    },
    /**
     * @param {Array} - array of strings to be filtered
     * @returns - Filtered Array of Strings
     *
     * removes prefix and filters all 3 types of object
     */
    removeListItems(val) {
      let removedPrefix = "";
      let filtered = val.filter(item => {
        if (!item.startsWith("bd_")) {
          removedPrefix = item.slice(3);
        }
        if (
          removedPrefix !== "stude_date" &&
          removedPrefix !== "deleted_at" &&
          removedPrefix !== "abandoned_date" &&
          item !== "bd_deleted_at"
        ) {
          return item;
        }
      });
      return filtered;
    },

    /**
     * @param {String} item - attributes unformated label
     * @returns {String} - Formated Label with capital letters
     */
    formatText(item, field) {
      let label = "";
      if (field === "extraFields") {
        label = item.replace(/_/g, " ");
      } else {
        label = item.slice(2).replace(/_/g, " ");
      }
      label = label.split(" ");
      for (let i = 0; i < label.length; i++) {
        label[i] = label[i].charAt(0).toUpperCase() + label[i].slice(1);
      }
      return label.join(" ");
    },

    /**
     * Fetch the objects of the selected layer within the workspace ordered by the selected attribute
     * @returns {Promise<void>}
     */
    async getObjectInWorkspaceOrderByAttribute() {
      try {
        if (this.selectedLayer && this.selectedAttribute) {
          this.accumulateLoading = true;
          this.allObjects = await this.fetchObjectInWorkspaceOrderByAttribute({
            layer: this.selectedLayer,
            attribute: this.selectedAttribute
          });

          this.dates = [];
          if (this.allObjects && this.allObjects.objects) {
            let dates = Object.keys(this.allObjects.objects);
            this.dates = dates.sort((a, b) => new Date(a) - new Date(b));
          }
          this.generateDataToSendHeatMap();
          this.accumulateLoading = false;
        }
      } catch (e) {
        console.error(e);
        this.accumulateLoading = false;
      }
    },

    /**
     * Generate data to send to map to generate HeatMap
     * Example ['{"type":"Point","coordinates":[-8.66135716438293,41.1562375075913]}']
     */
    generateDataToSendHeatMap() {
      if (this.selectedLayer && this.selectedAttribute && this.allObjects) {
        if (this.isToAccumulate) {
          let accumulatedDatesGeom = [];
          for (let i = 0; i < this.indexSelectedDate + 1; i++) {
            let date = this.dates[i];
            if (this.allObjects.objects[date]) {
              this.allObjects.objects[date].forEach(object => {
                let objectGeoJson = this.searchObjectField(object, "geo_json");
                accumulatedDatesGeom.push(objectGeoJson);
              });
            }
          }
          this.$emit("generate-heatmap", accumulatedDatesGeom);
        } else {
          let date = this.dates[this.indexSelectedDate];

          let objectsGeom = [];
          if (this.allObjects.objects[date]) {
            this.allObjects.objects[date].forEach(object => {
              let objectGeoJson = this.searchObjectField(object, "geo_json");
              objectsGeom.push(objectGeoJson);
            });
          }
          this.$emit("generate-heatmap", objectsGeom);
        }
      }
    },

    /**
     * Scroll the object to find the geom
     * @param object
     * @param field
     * @returns {{"type":"Point","coordinates":[-8.66135716438293,41.1562375075913]}}
     */
    searchObjectField(object, field) {
      for (const [key, value] of Object.entries(object)) {
        if (typeof value === "object" && value !== null) {
          let fieldFounded = this.searchObjectField(value, field);
          if (fieldFounded) return fieldFounded;
        }
        if (key.includes(field) && value) {
          return value;
        }
      }
    }
  }
};
</script>

<style>
.heatmap-toolbar-container {
  width: 50%;
  position: absolute;
  top: 15px;
  right: 15px;
  z-index: 7;
  background-color: white;
  border-radius: 5px;
}

.heatmap-toolbar-container .v-slider--horizontal .v-slider__thumb-label > * {
  transform: rotate(-226deg) !important;
  font-size: 9px;
  padding-left: 4px;
}

.heatmap-toolbar-container .v-slider--horizontal .v-slider__thumb-label {
  transform: translateY(100%) translateY(15px) translateX(-50%) rotate(-134deg) !important;
  bottom: 0 !important;
}
</style>



// WEBPACK FOOTER //
// src/components/Home/Toolbars/HeatmapToolbar.vue